// src/context/AudioPlayerContext.tsx

import React, { createContext, useContext, useRef, useState, ReactNode } from 'react';

interface AudioPlayerContextType {
  currentlyPlaying: string | null;
  setCurrentlyPlaying: (mediaId: string | null) => void;
  pauseAll: () => void;
  playExternal: (src: string, id?: string) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const AudioPlayerProvider = ({ children }: { children: ReactNode }) => {
  const [currentlyPlaying, setCurrentlyPlayingState] = useState<string | null>(null);
  const externalRef = useRef<HTMLAudioElement | null>(null);

  // Pause any audio that is not the one we're switching to
  const setCurrentlyPlayingSafe = (id: string | null) => {
    // if someone else starts, stop the external clip
    if (id !== '__welcome__' && externalRef.current) {
      externalRef.current.pause();
    }
    setCurrentlyPlayingState(id);
  };

  // Helper to play one-off sounds (welcome, notifications, etc.)
  const playExternal = (src: string, id = '__welcome__') => {
    if (externalRef.current) externalRef.current.pause();
    const a = new Audio(src);
    a.volume = 0.4;
    externalRef.current = a;
    setCurrentlyPlayingSafe(id);
    a.play().catch(console.warn);
    a.onended = () => setCurrentlyPlayingSafe(null);
  };

  const pauseAll = () => setCurrentlyPlayingSafe(null);

  return (
    <AudioPlayerContext.Provider value={{
      currentlyPlaying,
      setCurrentlyPlaying: setCurrentlyPlayingSafe,
      pauseAll,
      playExternal
    }}>
      {children}
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (context === undefined) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};